import { faXmark, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import {
  IconButton,
  InputAdornment,
  Select,
  SelectProps,
  SelectChangeEvent,
  PopoverOrigin,
  ListSubheaderProps,
  MenuItem,
} from "@mui/material";
import { useFormControl } from "@mui/material/FormControl";
import {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useState,
  useMemo,
  Children,
  isValidElement,
} from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import {
  SelectControlContext,
  SubmenuHeightContext,
  SUBMENU_TRANSITION_DURATION,
} from "./components/SrSelectContext";
import { SrSelectItem, SrSelectItemProps } from "./components/SrSelectItem";
import { SrSelectSubheader } from "./components/SrSelectSubheader";
import { SrSelectSubmenu, SrSelectSubmenuProps } from "./components/SrSelectSubmenu";

export type SrSelectProps = Omit<
  SelectProps,
  "name" | "label" | "placeholder" | "error" | "onChange"
> & {
  name: string;
  placeholder?: string | ReactNode | undefined;
  selectedItemTemplate?: ReactNode | ((value: string) => ReactNode);
  onChange: (v: string) => void;
  readOnly?: boolean;
  clearButton?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  anchorOrigin?: PopoverOrigin;
};

export interface SrSelectComponent extends FC<SrSelectProps> {
  Item: FC<SrSelectItemProps>;
  Subheader: FC<ListSubheaderProps>;
  Submenu: FC<SrSelectSubmenuProps>;
}

/**
 * Register all the Select values
 * Recursively find all SrSelect.Item components and extract their value and children.
 * @NOTE: This is needed because MUI Select needs to have all possible values.
 * And in case of the sub-menus, they are not direct children of the Select component.
 * So we need to traverse the tree to find them.
 */
type ItemData = { value: unknown; children: ReactNode };

const findSelectItems = (children: ReactNode): ItemData[] => {
  const items: ItemData[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) {
      return;
    }

    // Check if the child is our SrSelectItem
    if (child.type === SrSelectItem) {
      const { value, children: itemChildren } = child.props as SrSelectItemProps;
      if (value) {
        items.push({ value, children: itemChildren });
      }
    }

    // Recurse through children
    if (child.props.children) {
      items.push(...findSelectItems(child.props.children));
    }
  });

  return items;
};

export const SrSelect: SrSelectComponent = ({
  id: inId,
  children,
  multiline,
  value,
  name,
  placeholder,
  selectedItemTemplate,
  readOnly,
  disabled,
  onFocus,
  onBlur,
  onChange,
  inputProps,
  sx,
  clearButton,
  startAdornment,
  endAdornment,
  anchorOrigin,
  MenuProps,
  autoWidth,
  size: sizeProp,
}: SrSelectProps): ReactElement => {
  const formControl = useFormControl();
  const [isOpen, setIsOpen] = useState(false);
  const [menuHeight, setMenuHeight] = useState<number | undefined>(undefined);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLElement | null>(undefined);
  const menuPaperRef = useRef<HTMLDivElement | null>(null);

  const id = inId || name;
  const safeValue = (value ?? "") as string;
  const size = sizeProp ?? formControl?.size ?? "medium";

  const allItems = useMemo(() => findSelectItems(children), [children]);

  const ArrowDropdownIcon = useCallback(
    () => (
      <FaIcon
        className="icon-dropdown-arrow"
        icon={faChevronDown}
        fontSize="inherit"
        sx={(theme) => ({
          visibility: "visible",
          pointerEvents: "none",
          userSelect: "none",
          position: "absolute",
          right: 15,
          transform: isOpen ? "rotate(180deg)" : "rotate(0)",
          color: theme.vars.palette.on.surfaceDimmer,
        })}
      />
    ),
    [isOpen],
  );

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    onChange(e.target.value as string);
  };
  const handleReset = () => onChange("");

  const getRenderValue = (): (() => ReactNode) | undefined => {
    if (safeValue !== "") {
      let customRender: ReactNode = null;

      // If selectedItemTemplate is a function, call it with the value
      if (typeof selectedItemTemplate === "function") {
        customRender = selectedItemTemplate(safeValue);
      } else if (selectedItemTemplate) {
        customRender = selectedItemTemplate;
      }

      if (customRender) {
        return () => customRender;
      }

      // Fallback to rendering the item's children
      const selectedItem = allItems.find((item) => item.value === safeValue);
      if (selectedItem) {
        return () => selectedItem.children;
      }
    }
    if (placeholder != null) return () => placeholder;
    return () => "";
  };

  const openSubmenu = useCallback((panelHeight: number) => {
    setMenuHeight(panelHeight);
  }, []);

  const closeSubmenu = useCallback(() => {
    setMenuHeight(0);
  }, []);

  const submenuHeightCtxValue = useMemo(
    () => ({ openSubmenu, closeSubmenu }),
    [openSubmenu, closeSubmenu],
  );

  const handleItemClick = useCallback(
    (itemValue: unknown) => {
      onChange(itemValue as string);
      setIsOpen(false);
      setMenuWidth(undefined);
      setMenuHeight(undefined);
    },
    [onChange],
  );

  const selectControlCtxValue = useMemo(() => ({ handleItemClick }), [handleItemClick]);

  return (
    <div ref={rootRef} style={{ width: "100%" }}>
      <SelectControlContext.Provider value={selectControlCtxValue}>
        <SubmenuHeightContext.Provider value={submenuHeightCtxValue}>
          <Select
            ref={selectRef}
            data-testid={`${id}-root`}
            multiline={multiline}
            size={size}
            autoWidth={autoWidth}
            open={isOpen}
            IconComponent={ArrowDropdownIcon}
            inputProps={{
              "data-testid": `${id}-input`,
              ...inputProps,
            }}
            id={id}
            name={name}
            value={safeValue}
            // @ts-expect-error allow ReactNode placeholder
            placeholder={placeholder}
            displayEmpty={!!placeholder}
            renderValue={getRenderValue()}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            onOpen={() => {
              if (disabled || readOnly) return; // prevent opening when not allowed
              setMenuWidth(autoWidth ? undefined : rootRef.current?.getBoundingClientRect().width);
              setIsOpen(true);
            }}
            onClose={() => {
              setIsOpen(false);
              setMenuWidth(undefined);
              setMenuHeight(undefined);
            }}
            onChange={handleChange}
            readOnly={readOnly}
            SelectDisplayProps={{
              "aria-readonly": readOnly ? "true" : undefined,
            }}
            MenuProps={{
              ...MenuProps,
              ...(anchorOrigin ? { anchorOrigin } : {}),
              MenuListProps: {
                ...MenuProps?.MenuListProps,
                sx: [
                  {
                    overflow: "hidden",
                    height: "100%",
                  },
                  ...(Array.isArray(MenuProps?.MenuListProps?.sx)
                    ? MenuProps.MenuListProps.sx
                    : [MenuProps?.MenuListProps?.sx]),
                ],
              },
              PaperProps: {
                ...MenuProps?.PaperProps,
                ref: menuPaperRef,
                sx: [
                  {
                    overflowX: "hidden",
                    overflowY: "auto",
                    ...(!autoWidth && menuWidth
                      ? {
                          width: menuWidth,
                          minWidth: menuWidth,
                        }
                      : {}),
                    ...(menuHeight
                      ? {
                          height: menuHeight,
                          transition: `height ${SUBMENU_TRANSITION_DURATION} ease`,
                        }
                      : {}),
                  },
                  ...(Array.isArray(MenuProps?.PaperProps?.sx)
                    ? MenuProps.PaperProps.sx
                    : [MenuProps?.PaperProps?.sx]),
                ],
              },
            }}
            startAdornment={
              startAdornment ? (
                <InputAdornment position="start">{startAdornment}</InputAdornment>
              ) : null
            }
            endAdornment={
              endAdornment || clearButton ? (
                <InputAdornment position="end" sx={{ mr: 3.5 }}>
                  {endAdornment}
                  {clearButton && value ? (
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReset();
                      }}
                    >
                      <FaIcon icon={faXmark} />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ) : null
            }
            sx={[
              /*
               * Select Item styles
               * for the inner MenuItem layout
               * to support a multi-row version with icons.
               */
              {
                width: "100%",
                "& > div > div > div": { width: "100%" },
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
          >
            {/* Render original children to preserve structure and submenus */}
            {children}
            {/* Render all possible items hidden for MUI to register values */}
            {allItems.map((item) => (
              <MenuItem
                key={item.value as string}
                value={item.value as string}
                sx={{ display: "none", p: 0, height: 0 }}
              />
            ))}
          </Select>
        </SubmenuHeightContext.Provider>
      </SelectControlContext.Provider>
    </div>
  );
};

SrSelect.Item = SrSelectItem;
SrSelect.Subheader = SrSelectSubheader;
SrSelect.Submenu = SrSelectSubmenu;

SrSelect.displayName = "SrSelect";
